import { z } from 'zod';

/** A decimal-as-string quantity (matches the stock layer's string decimals). */
const qty = z.string().regex(/^-?\d+(\.\d+)?$/, 'must be a number');

/** Stocktake header in a list. */
export interface StocktakeSummary {
  id: string;
  docNumber: string;
  warehouseId: string;
  warehouseName: string;
  status: 'draft' | 'posted' | 'cancelled';
  note: string | null;
  lineCount: number;
  countedCount: number;
  /** Net valued variance (Σ variance×avgCost) in the snapshot's currency, for display. */
  varianceValue: string;
  createdAt: string;
  postedAt: string | null;
}

/** One count line: snapshot system qty + the counted qty + derived variance. */
export interface StocktakeLine {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  systemQty: string;
  countedQty: string | null;
  avgCost: string;
  avgCostCurrencyCode: string | null;
  /** countedQty − systemQty (0 when not yet counted). */
  varianceQty: string;
  /** varianceQty × avgCost. */
  varianceValue: string;
}

export interface StocktakeDetail extends Omit<StocktakeSummary, 'lineCount' | 'countedCount' | 'varianceValue'> {
  lines: StocktakeLine[];
}

/** Create a stocktake — snapshots current on-hand for the warehouse. */
export const StocktakeCreateSchema = z.object({
  warehouseId: z.string().uuid(),
  note: z.string().max(500).optional(),
});
export type StocktakeCreate = z.infer<typeof StocktakeCreateSchema>;

/** Save counted quantities (partial — only the lines the user has entered). */
export const StocktakeCountSchema = z.object({
  counts: z.array(z.object({
    lineId: z.string().uuid(),
    countedQty: qty.nullable(),
  })).max(5000),
});
export type StocktakeCount = z.infer<typeof StocktakeCountSchema>;
